import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    body: 'Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre, dirección de correo electrónico e información de pago. No vendemos, alquilamos ni compartimos tu información personal con terceros para fines de marketing.'
  }, {
    heading: 'Uso de la Información',
    body: 'Utilizamos tu información para procesar tu compra, enviarte el producto digital adquirido, comunicarnos contigo sobre tu pedido y mejorar nuestros productos y servicios.'
  }, {
    heading: 'Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Todos los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
  }, {
    heading: 'Tus Derechos',
    body: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales disponibles en nuestro sitio.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los Términos',
    body: 'Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, aceptas estar sujeto a estos términos y condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestro sitio.'
  }, {
    heading: 'Productos Digitales',
    body: 'Los productos ofrecidos son guías digitales en formato PDF. Una vez completada la compra, recibirás acceso inmediato al material. Debido a la naturaleza digital del producto, todas las ventas son finales.'
  }, {
    heading: 'Uso Permitido',
    body: 'El contenido adquirido es para tu uso personal únicamente. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Resultados',
    body: 'Los resultados mencionados en nuestro sitio son ejemplos ilustrativos y no constituyen una garantía de ingresos. Los resultados individuales pueden variar según el esfuerzo, dedicación y circunstancias de cada persona.'
  }, {
    heading: 'Limitación de Responsabilidad',
    body: 'No nos hacemos responsables de daños directos, indirectos, incidentales o consecuentes que resulten del uso o la imposibilidad de uso de nuestros productos digitales. El uso del material es bajo tu propia responsabilidad.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio web.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-navy-900 border-t border-slate-800 py-8 px-4" data-id="element-41">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-42">
          <p className="text-slate-500 text-sm" data-id="element-43">
            © {new Date().getFullYear()} Anti-Inflación USA. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-44">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-400 hover:text-gold text-sm transition-colors duration-200" data-id="element-45">
              Política de Privacidad
            </button>
            <span className="text-slate-700" data-id="element-46">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-slate-400 hover:text-gold text-sm transition-colors duration-200" data-id="element-47">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-48">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-49">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-50" />

            {/* Modal content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={(e: React.MouseEvent) => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden z-10" data-id="element-51">
              {/* Header */}
              <div className="sticky top-0 bg-navy-900 px-6 py-5 flex items-center justify-between z-10" data-id="element-52">
                <h2 className="text-xl font-bold text-white" data-id="element-53">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-slate-400 hover:text-white transition-colors p-1" aria-label="Cerrar" data-id="element-54">
                  <XIcon className="w-6 h-6" data-id="element-55" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-8 overflow-y-auto max-h-[calc(85vh-72px)] space-y-6" data-id="element-56">
                {content.sections.map((section, index) => <div key={index} data-id="element-57">
                    <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-58">
                      {section.heading}
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-59">
                      {section.body}
                    </p>
                  </div>)}

                <div className="pt-4 border-t border-slate-200" data-id="element-60">
                  <p className="text-sm text-slate-400" data-id="element-61">
                    Última actualización: Marzo 2026
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}